/**
 * Copyright (c) 2025 Répét Contributors
 * Licensed under the MIT License
 * See LICENSE file in the project root for full license text
 */

import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import './styles/globals.css'
import { initializeDatabase } from './core/storage'
import { ttsEngine } from './core/tts'
import { enablePWADebug } from './utils/pwa-debug'
import { useAuthStore } from './state/authStore'
import { logVersionInfo } from './config/version'
import { startDownloadMonitoring } from './services/compilationService'

// Exposer les outils de debug PWA (disponible via window.pwaDebug)
enablePWADebug()
console.warn('[Main] 🔍 Outils de debug PWA disponibles via window.pwaDebug')
console.warn('[Main] Exemple: window.pwaDebug.printDebugReport()')

// Afficher les informations de version au démarrage
logVersionInfo()

/**
 * Calcule et applique la vraie hauteur viewport
 * Résout le problème sur mobile où 100vh inclut la barre d'URL du navigateur
 */
let viewportHeightTimeout: number | null = null

function setViewportHeight() {
  // Utiliser visualViewport.height si disponible : c'est la hauteur VISIBLE réelle,
  // qui tient compte de la barre d'adresse du navigateur (sur Chrome Android,
  // window.innerHeight = grand viewport sans barre, visualViewport.height = zone visible).
  const appHeight = window.visualViewport?.height ?? window.innerHeight
  const vh = appHeight * 0.01

  // Définir les variables CSS personnalisées
  document.documentElement.style.setProperty('--vh', `${vh}px`)
  document.documentElement.style.setProperty('--app-height', `${appHeight}px`)

  // Log seulement lors du chargement initial (pas à chaque resize)
  if (viewportHeightTimeout === null) {
    console.warn(`[Main] 📐 Hauteur viewport calculée: ${appHeight}px (1vh = ${vh}px)`)
  }
}

// Debounce pour éviter trop de recalculs pendant le resize
function debouncedSetViewportHeight() {
  if (viewportHeightTimeout !== null) {
    clearTimeout(viewportHeightTimeout)
  }
  viewportHeightTimeout = window.setTimeout(() => {
    setViewportHeight()
    viewportHeightTimeout = null
  }, 150)
}

// Calculer la hauteur viewport au chargement
setViewportHeight()

// Recalculer lors du redimensionnement (changement d'orientation, apparition/disparition de la barre d'URL)
window.addEventListener('resize', debouncedSetViewportHeight)
window.addEventListener('orientationchange', setViewportHeight)

// visualViewport.resize : événement précis déclenché quand la barre d'adresse
// apparaît/disparaît sur Chrome Android et iOS Safari (window.resize ne se déclenche
// pas toujours dans ce cas avec interactive-widget=resizes-visual).
window.visualViewport?.addEventListener('resize', debouncedSetViewportHeight)
// visualViewport.scroll : sur iOS, la barre peut changer lors du scroll
window.visualViewport?.addEventListener('scroll', debouncedSetViewportHeight)

// Sur iOS, également écouter les événements de scroll qui peuvent changer la hauteur
// (la barre d'URL apparaît/disparaît pendant le scroll)
let resizeTimeout: number | null = null
window.addEventListener(
  'scroll',
  () => {
    if (resizeTimeout) clearTimeout(resizeTimeout)
    resizeTimeout = window.setTimeout(setViewportHeight, 100)
  },
  { passive: true }
)

// Fonction d'initialisation asynchrone
async function initializeApp() {
  try {
    console.warn('[Main] 🚀 Initialisation de la base de données et du moteur TTS (stub V2)...')

    // Initialiser la base de données, le stub TTS et l'authentification
    await Promise.all([
      initializeDatabase(),
      ttsEngine.initialize(),
      useAuthStore.getState().initialize(),
    ])

    console.warn('[Main] ✅ Base de données initialisée (TTS désactivé en V2.0)')
    console.warn('[Main] ✅ Authentification initialisée')
    console.warn('[Main] ✅ Application prête à démarrer')

    // Démarrer le monitoring des téléchargements bloqués (Phase 3)
    startDownloadMonitoring()

    // Rendre l'application React APRÈS l'initialisation complète
    ReactDOM.createRoot(document.getElementById('root')!).render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    )
  } catch (error) {
    console.error("[Main] ❌ Erreur fatale lors de l'initialisation:", error)

    // Afficher un message d'erreur dans le DOM
    const root = document.getElementById('root')
    if (root) {
      root.innerHTML = `
        <div style="
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          background: linear-gradient(135deg, #1a202c 0%, #2d3748 100%);
          font-family: system-ui, -apple-system, sans-serif;
        ">
          <div style="
            background: rgba(254, 202, 202, 0.1);
            border: 2px solid #fc8181;
            border-radius: 12px;
            padding: 2rem;
            max-width: 500px;
            margin: 1rem;
          ">
            <h2 style="color: #feb2b2; font-size: 1.5rem; margin-bottom: 1rem;">
              ⚠️ Erreur d'initialisation
            </h2>
            <p style="color: #fbd5d5; margin-bottom: 1.5rem;">
              ${error instanceof Error ? error.message : 'Erreur inconnue'}
            </p>
            <button
              onclick="window.location.reload()"
              style="
                background: #e53e3e;
                color: white;
                padding: 0.75rem 1.5rem;
                border: none;
                border-radius: 6px;
                font-size: 1rem;
                cursor: pointer;
                transition: background 0.2s;
              "
              onmouseover="this.style.background='#c53030'"
              onmouseout="this.style.background='#e53e3e'"
            >
              Recharger la page
            </button>
          </div>
        </div>
      `
    }
  }
}

// Démarrer l'initialisation
initializeApp()
